// React Query hook for the RGS per-report Sources read API (RGS-3). Feeds the
// reputation readout card (RGS-4) on the report dashboard and, later, the
// Sources page (RGS-5). Same auth/workspace gating as the buyer reads.

import { useQuery } from "@tanstack/react-query";
import { vbClient } from "@/services/vb/vbClient";
import { useAuth } from "@/context/AuthContext";
import { vbKeys, useVbWorkspaceScope } from "@/hooks/vb/useBuyers";
import type { ReportSourcesResponse } from "@/types/vb";

/** GET /reports/<id>/sources/ for one report.
 *
 *  Gated on `token` (avoids the cold-load 401 race) + `wsReady` (the endpoint is
 *  workspace-scoped; firing before WorkspaceContext resolves would hit the
 *  personal-workspace fallback). `enabled` lets the caller stay disabled for
 *  free reports, where the backend would 403 (paid-only). */
export function useReportSources(reportId: number | undefined, enabled = true) {
  const { token } = useAuth();
  const { wsKey, wsReady } = useVbWorkspaceScope();
  return useQuery<ReportSourcesResponse>({
    queryKey: [...vbKeys.reportSources(reportId ?? -1), { ws: wsKey }],
    queryFn: () => vbClient.getReportSources(reportId as number, token),
    enabled: enabled && !!reportId && !!token && wsReady,
    staleTime: 5 * 60 * 1000,
  });
}
